import React, {useState, useEffect, useRef, useCallback, JSX} from 'react';
import {useClickable} from '../providers/ClickableProvider';
import {useNavigate} from 'react-router-dom';
import {useTheme} from '../contexts/ThemeContext';
import {useAuth} from '../auth/AuthContext';
import api from '../api/axios';
import '../styles/Header.css';
import {
    LogIn, LogOut, Bell, UserPlus, UserCheck, Trash2, Search, Settings,
    User, Eye, EyeOff, HelpCircle, ChevronDown, Sun, Moon, X
} from 'lucide-react';

enum AnimeStatus {
    ONGOING = 'ONGOING',
    COMPLETED = 'COMPLETED',
    UPCOMING = 'UPCOMING'
}

interface AgeRatingOption {
    code: string;
    displayName: string;
    description: string;
}

interface Genre {
    id: number;
    name: string;
}

interface Studio {
    id: number;
    name: string;
}

interface Anime {
    id: number;
    title: string;
    imageUrl: string;
    episodes: number;
}

interface UserSearchResult {
    id: number;
    nickname: string;
    avatarUrl: string;
}

interface CreateAnimeData {
    title: string;
    imageFolder: string;
    episodes: string;
    description: string;
    releaseDate: string;
    animeStatus: AnimeStatus;
    selectedGenres: number[];
    selectedStudios: number[];
    sourceMaterial: string;
    ageRating: string;
}

type AuthMode = 'login' | 'register';

function useDebounce(value: string, delay: number) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

type AnimeModalMode = 'create' | 'edit';

interface NotificationItemProps {
    notification: any;
    onNotificationClick: (notification: any) => void;
    onDelete: (notificationId: number) => void;
    formatNotificationTime: (timestamp: string) => string;
    getNotificationIcon: (type: string) => JSX.Element;
    getNotificationText: (notification: any) => string;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
                                                               notification,
                                                               onNotificationClick,
                                                               onDelete,
                                                               formatNotificationTime,
                                                               getNotificationIcon,
                                                               getNotificationText
                                                           }) => {
    const {makeClickable} = useClickable();

    // Получаем URL для уведомления
    const getNotificationUrl = (): string => {
        switch (notification.type) {
            case 'FRIEND_REQUEST_RECEIVED':
            case 'FRIEND_REQUEST_ACCEPTED':
                return notification.relatedUser?.nickname
                    ? `/users/${notification.relatedUser.nickname}`
                    : '#';
            case 'NEW_EPISODE_AVAILABLE':
                return notification.anime?.id
                    ? `/anime/${notification.anime.id}`
                    : '#';
            default:
                return '#';
        }
    };

    const notificationUrl = getNotificationUrl();

    return (
        <div
            className={`notification-item ${notification.read ? 'read' : 'unread'}`}
            {...makeClickable(
                notificationUrl,
                (e) => {
                    // Вызываем существующий обработчик
                    onNotificationClick(notification);
                }
            )}
        >
            <div className="notification-icon-wrapper">
                {getNotificationIcon(notification.type)}
            </div>

            <div className="notification-content">
                <p className="notification-text">
                    {getNotificationText(notification)}
                </p>
                <span className="notification-time">
                    {formatNotificationTime(notification.createdAt)}
                </span>
            </div>

            <button
                className="notification-delete-btn"
                onClick={(e) => {
                    e.stopPropagation(); // Важно! Останавливаем всплытие
                    onDelete(notification.id);
                }}
                title="Удалить"
            >
                <Trash2 size={14}/>
            </button>
        </div>
    );
};

const Header: React.FC = () => {
    const {isAuthenticated, logout, login, role, user} = useAuth();
    const {theme, toggleTheme} = useTheme();
    const navigate = useNavigate();
    const {makeClickable} = useClickable();
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500);
    const [searchResults, setSearchResults] = useState<Anime[]>([]);
    const [showNoResults, setShowNoResults] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showSearchModal, setShowSearchModal] = useState(false);
    const [authMode, setAuthMode] = useState<AuthMode>('login');
    const [loginData, setLoginData] = useState({
        username: '',
        password: '',
        rememberMe: false
    });
    const [registerData, setRegisterData] = useState({
        username: '',
        nickname: '',
        password: '',
        confirmPassword: ''
    });
    const [loginErrors, setLoginErrors] = useState<{ [key: string]: string }>({});
    const [registerErrors, setRegisterErrors] = useState<{ [key: string]: string }>({});
    const [registerError, setRegisterError] = useState<string | null>(null);
    const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
    const modalRef = useRef<HTMLDivElement>(null);
    const searchModalRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const settingsRef = useRef<HTMLDivElement>(null);
    const [showCreateAnimeModal, setShowCreateAnimeModal] = useState(false);
    const [createAnimeData, setCreateAnimeData] = useState<CreateAnimeData>({
        title: '',
        imageFolder: '',
        episodes: '',
        description: '',
        releaseDate: '',
        animeStatus: AnimeStatus.ONGOING,
        selectedGenres: [],
        selectedStudios: [],
        sourceMaterial: '',
        ageRating: ''
    });
    const [createAnimeErrors, setCreateAnimeErrors] = useState<{ [key: string]: string }>({});
    const [createAnimeLoading, setCreateAnimeLoading] = useState(false);
    const [genres, setGenres] = useState<Genre[]>([]);
    const [genresLoading, setGenresLoading] = useState(false);
    const [animeModalMode, setAnimeModalMode] = useState<AnimeModalMode>('create');
    const [searchAnimeQuery, setSearchAnimeQuery] = useState('');
    const [searchAnimeResults, setSearchAnimeResults] = useState<Anime[]>([]);
    const [selectedAnimeForEdit, setSelectedAnimeForEdit] = useState<Anime | null>(null);
    const [searchAnimeLoading, setSearchAnimeLoading] = useState(false);
    const debouncedSearchAnimeQuery = useDebounce(searchAnimeQuery, 500);
    const [showNotifications, setShowNotifications] = useState(false);
    const [notifications, setNotifications] = useState<any[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [notificationsLoading, setNotificationsLoading] = useState(false);
    const notificationsRef = useRef<HTMLDivElement>(null);
    const [notificationTab, setNotificationTab] = useState<'unread' | 'read'>('unread');
    const [searchMode, setSearchMode] = useState<'anime' | 'users'>('anime');
    const [userSearchResults, setUserSearchResults] = useState<UserSearchResult[]>([]);
    const [showNoUserResults, setShowNoUserResults] = useState(false);
    const [showMinCharsMessage, setShowMinCharsMessage] = useState(false);
    const [totalAnimeResults, setTotalAnimeResults] = useState(0);
    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [showRegisterPassword, setShowRegisterPassword] = useState(false);
    const [showRegisterConfirmPassword, setShowRegisterConfirmPassword] = useState(false);
    const [showHelp, setShowHelp] = useState<{ [key: string]: boolean }>({
        username: false,
        nickname: false,
        password: false,
        confirmPassword: false
    });
    const [checkingUsernameAvailability, setCheckingUsernameAvailability] = useState(false);
    const [checkingNicknameAvailability, setCheckingNicknameAvailability] = useState(false);
    const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
    const [nicknameAvailable, setNicknameAvailable] = useState<boolean | null>(null);
    const usernameCheckTimeout = useRef<NodeJS.Timeout | null>(null);
    const nicknameCheckTimeout = useRef<NodeJS.Timeout | null>(null);
    const [showHelpButtonVisible, setIsHelpButtonVisible] = useState<{ [key: string]: boolean }>({
        username: true,
        nickname: true,
        password: true,
        confirmPassword: true
    });
    const [studios, setStudios] = useState<Studio[]>([]);
    const [studiosLoading, setStudiosLoading] = useState(false);
    const [ageRatings, setAgeRatings] = useState<AgeRatingOption[]>([]);
    const [ageRatingsLoading, setAgeRatingsLoading] = useState(false);
    const [showAgeRatingDropdown, setShowAgeRatingDropdown] = useState(false);
    const ageRatingDropdownRef = useRef<HTMLDivElement>(null);

    const [searchGenreQuery, setSearchGenreQuery] = useState('');
    const [searchGenreResults, setSearchGenreResults] = useState<Genre[]>([]);
    const [searchGenreLoading, setSearchGenreLoading] = useState(false);
    const debouncedSearchGenreQuery = useDebounce(searchGenreQuery, 500);

    const [searchStudioQuery, setSearchStudioQuery] = useState('');
    const [searchStudioResults, setSearchStudioResults] = useState<Studio[]>([]);
    const [searchStudioLoading, setSearchStudioLoading] = useState(false);
    const debouncedSearchStudioQuery = useDebounce(searchStudioQuery, 500);

    const [showGenreSearch, setShowGenreSearch] = useState(false);
    const [showStudioSearch, setShowStudioSearch] = useState(false);


    // Функция для поиска жанров
    const fetchGenreSearchResults = useCallback(async (query: string) => {
        if (!query.trim()) {
            setSearchGenreResults([]);
            return;
        }

        try {
            setSearchGenreLoading(true);
            const response = await api.get(`/genres/search?name=${encodeURIComponent(query)}`);
            setSearchGenreResults(response.data || []);
        } catch (error) {
            console.error('Search genres error:', error);
            setSearchGenreResults([]);
        } finally {
            setSearchGenreLoading(false);
        }
    }, []);

    // Функция для поиска студий
    const fetchStudioSearchResults = useCallback(async (query: string) => {
        if (!query.trim()) {
            setSearchStudioResults([]);
            return;
        }

        try {
            setSearchStudioLoading(true);
            const response = await api.get(`/studios/search?name=${encodeURIComponent(query)}`);
            setSearchStudioResults(response.data || []);
        } catch (error) {
            console.error('Search studios error:', error);
            setSearchStudioResults([]);
        } finally {
            setSearchStudioLoading(false);
        }
    }, []);

    // Эффект для поиска жанров
    useEffect(() => {
        if (debouncedSearchGenreQuery.trim() && showGenreSearch) {
            fetchGenreSearchResults(debouncedSearchGenreQuery);
        } else {
            setSearchGenreResults([]);
        }
    }, [debouncedSearchGenreQuery, showGenreSearch, fetchGenreSearchResults]);

    // Эффект для поиска студий
    useEffect(() => {
        if (debouncedSearchStudioQuery.trim() && showStudioSearch) {
            fetchStudioSearchResults(debouncedSearchStudioQuery);
        } else {
            setSearchStudioResults([]);
        }
    }, [debouncedSearchStudioQuery, showStudioSearch, fetchStudioSearchResults]);

    const fetchStudios = useCallback(async () => {
        try {
            setStudiosLoading(true);
            const response = await api.get('/studios');
            setStudios(response.data);
        } catch (error) {
            console.error('Error fetching studios:', error);
        } finally {
            setStudiosLoading(false);
        }
    }, []);

    const fetchAgeRatings = useCallback(async () => {
        try {
            setAgeRatingsLoading(true);
            const response = await api.get('/age-ratings');
            setAgeRatings(response.data);
        } catch (error) {
            console.error('Error fetching age ratings:', error);
        } finally {
            setAgeRatingsLoading(false);
        }
    }, []);

    const fetchUserSearchResults = useCallback(async (query: string) => {
        const trimmedQuery = query.trim();

        if (!trimmedQuery) {
            setUserSearchResults([]);
            setShowNoUserResults(false);
            setShowMinCharsMessage(false);
            return;
        }

        // Проверка минимальной длины
        if (trimmedQuery.length < 2) {
            setUserSearchResults([]);
            setShowNoUserResults(false);
            setShowMinCharsMessage(true);
            return;
        }

        setShowMinCharsMessage(false);

        try {
            const response = await api.get(`/users/search?nickname=${encodeURIComponent(trimmedQuery)}&page=0&size=6`);
            const users = Array.isArray(response.data) ? response.data : [];

            // Берем только первые 5 результатов
            const limitedUsers = users.slice(0, 6);

            const publicUsers = limitedUsers.map((user: any) => ({
                id: user.id,
                nickname: user.nickname,
                avatarUrl: user.avatarUrl || '/user-images/default/avatar.jpg'
            }));

            setUserSearchResults(publicUsers);
            setShowNoUserResults(publicUsers.length === 0);

        } catch (error: any) {
            setUserSearchResults([]);
            setShowNoUserResults(true);
            setShowMinCharsMessage(false);
        }
    }, []);

    // Загрузка уведомлений
    const fetchNotifications = useCallback(async (tab: 'unread' | 'read' = 'unread') => {
        if (!isAuthenticated) return;

        try {
            setNotificationsLoading(true);
            const response = await api.get('/user/notifications');
            const allNotifications = response.data;

            // Разделяем уведомления на прочитанные и непрочитанные
            const unreadNotifications = allNotifications.filter((n: any) => !n.read);
            const readNotifications = allNotifications.filter((n: any) => n.read);

            // Обновляем счетчик непрочитанных
            setUnreadCount(unreadNotifications.length);

            // Сохраняем ВСЕ уведомления (для переключения вкладок без нового запроса)
            setNotifications(allNotifications);

            // Также можно сохранять раздельно, если нужно
            // setUnreadNotifications(unreadNotifications);
            // setReadNotifications(readNotifications);

        } catch (error) {
            console.error('Error fetching notifications:', error);
            setNotifications([]);
            setUnreadCount(0);
        } finally {
            setNotificationsLoading(false);
        }
    }, [isAuthenticated]);

    // Обновление счетчика уведомлений
    const updateNotificationCount = useCallback(async () => {
        if (!isAuthenticated) return;

        try {
            const response = await api.get('/user/notifications/count');
            setUnreadCount(response.data.unreadCount || 0);
        } catch (error) {
            console.error('Error fetching notification count:', error);
        }
    }, [isAuthenticated]);

    // Пометка уведомления как прочитанного
    const markAsRead = async (notificationId: number) => {
        try {
            await api.post(`/user/notifications/${notificationId}/mark-read`);
            // Обновляем локальное состояние
            setNotifications(prev =>
                prev.map(n =>
                    n.id === notificationId ? {...n, read: true} : n
                )
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    // Пометка всех как прочитанных
    const markAllAsRead = async () => {
        try {
            await api.post('/user/notifications/mark-all-read');
            setNotifications(prev => prev.map(n => ({...n, read: true})));
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    // Удаление уведомления
    const deleteNotification = async (notificationId: number) => {
        try {
            await api.delete(`/user/notifications/${notificationId}`);
            setNotifications(prev => prev.filter(n => n.id !== notificationId));
            // Проверяем, было ли уведомление непрочитанным
            const notification = notifications.find(n => n.id === notificationId);
            if (notification && !notification.read) {
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (error) {
            console.error('Error deleting notification:', error);
        }
    };

    // Обработчик клика по уведомлению
    const handleNotificationClick = (notification: any) => {
        // Пометим как прочитанное
        if (!notification.read) {
            markAsRead(notification.id);
        }

        // Переход в зависимости от типа уведомления
        switch (notification.type) {
            case 'FRIEND_REQUEST_RECEIVED':
            case 'FRIEND_REQUEST_ACCEPTED':
                if (notification.relatedUser?.nickname) {
                    navigate(`/users/${notification.relatedUser.nickname}`);
                }
                break;
            case 'NEW_EPISODE_AVAILABLE':
                if (notification.anime?.id) {
                    navigate(`/anime/${notification.anime.id}`);
                }
                break;
            default:
                break;
        }

        setShowNotifications(false);
    };

    const handleAgeRatingSelect = (ratingCode: string) => {
        setCreateAnimeData(prev => ({
            ...prev,
            ageRating: ratingCode
        }));
        setShowAgeRatingDropdown(false);
    };

    // Получение иконки для типа уведомления
    const getNotificationIcon = (type: string) => {
        switch (type) {
            case 'FRIEND_REQUEST_RECEIVED':
                return <UserPlus size={18} color="#21e2ff"/>;
            case 'FRIEND_REQUEST_ACCEPTED':
                return <UserCheck size={18} color="#8fff3d"/>;
            case 'NEW_EPISODE_AVAILABLE':
                return <Bell size={18} color="#ff8c0e"/>;
            default:
                return <Bell size={18} color="#cd69ff"/>;
        }
    };

    // Получение текста для уведомления
    const getNotificationText = (notification: any) => {
        switch (notification.type) {
            case 'FRIEND_REQUEST_RECEIVED':
                return `@${notification.relatedUser?.nickname} хочет добавить вас в друзья`;
            case 'FRIEND_REQUEST_ACCEPTED':
                return `@${notification.relatedUser?.nickname} принял(а) ваш запрос в друзья`;
            case 'NEW_EPISODE_AVAILABLE':
                return `Доступна ${notification.episodeNumber} серия "${notification.anime?.title}"`;
            default:
                return notification.message;
        }
    };

    // Форматирование времени
    const formatNotificationTime = (timestamp: string) => {

        const date = new Date(timestamp + 'Z');

        const now = new Date();

        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'только что';
        if (diffMins < 60) return `${diffMins} мин назад`;
        if (diffHours < 24) return `${diffHours} ч назад`;
        if (diffDays < 7) return `${diffDays} д назад`;

        return date.toLocaleDateString('ru-RU', {
            day: 'numeric',
            month: 'short'
        });
    };

    useEffect(() => {
        return () => {
            if (usernameCheckTimeout.current) {
                clearTimeout(usernameCheckTimeout.current);
            }
            if (nicknameCheckTimeout.current) {
                clearTimeout(nicknameCheckTimeout.current);
            }
        };
    }, []);

    // === ЭФФЕКТЫ ДЛЯ УВЕДОМЛЕНИЙ ===

    // Загрузка уведомлений при монтировании и изменении авторизации
    useEffect(() => {
        if (isAuthenticated) {
            fetchNotifications();
        } else {
            setNotifications([]);
            setUnreadCount(0);
        }
    }, [isAuthenticated, fetchNotifications]);

    // Обновление счетчика каждые 30 секунд (опционально)
    useEffect(() => {
        if (!isAuthenticated) return;

        const interval = setInterval(() => {
            updateNotificationCount();
        }, 30000); // 30 секунд

        return () => clearInterval(interval);
    }, [isAuthenticated, updateNotificationCount]);

    // Проверка прав администратора
    const isAdmin = role === 'ADMIN';

    const fetchGenres = useCallback(async () => {
        try {
            setGenresLoading(true);
            // Предполагаем, что у вас есть endpoint для получения жанров
            const response = await api.get('/genres');
            // Добавляем сортировку по алфавиту
            const sortedGenres = response.data.sort((a: Genre, b: Genre) =>
                a.name.localeCompare(b.name)
            );
            setGenres(sortedGenres); // ← меняем на отсортированный массив
        } catch (error) {
            console.error('Error fetching genres:', error);
        } finally {
            setGenresLoading(false);
        }
    }, []);

    useEffect(() => {
        if (showCreateAnimeModal) {
            if (genres.length === 0) {
                fetchGenres();
            }
            if (studios.length === 0) {
                fetchStudios();
            }
            if (ageRatings.length === 0) {
                fetchAgeRatings();
            }
        }
    }, [showCreateAnimeModal, genres.length, studios.length,
        ageRatings.length, fetchGenres, fetchStudios, fetchAgeRatings]);

    const fetchAnimeForEdit = useCallback(async (query: string) => {
        if (!query.trim()) {
            setSearchAnimeResults([]);
            return;
        }

        try {
            setSearchAnimeLoading(true);
            const response = await api.get(`/anime/search?title=${encodeURIComponent(query)}&page=0&size=5`);
            let results = response.data;
            if (response.data && response.data.content) {
                results = response.data.content;
            }
            setSearchAnimeResults(results);
        } catch (error: any) {
            console.error('Search anime for edit error:', error);
            setSearchAnimeResults([]);
        } finally {
            setSearchAnimeLoading(false);
        }
    }, []);

    useEffect(() => {
        if (debouncedSearchAnimeQuery.trim() && animeModalMode === 'edit') {
            fetchAnimeForEdit(debouncedSearchAnimeQuery);
        } else {
            setSearchAnimeResults([]);
        }
    }, [debouncedSearchAnimeQuery, animeModalMode, fetchAnimeForEdit]);

    const formatAgeRating = (rating: string): string => {
        if (!rating) return '';

        // Создаем карту для преобразования
        const ratingMap: { [key: string]: string } = {
            'PG_13': 'PG-13',
            'R_17': 'R-17',
            'R_PLUS': 'R+',
        };

        // Если есть в карте - возвращаем преобразованное значение
        if (ratingMap[rating]) {
            return ratingMap[rating];
        }

        return rating;
    };

    const handleProfileClick = () => {
        navigate('/profile');
    };

    const handleSelectAnimeForEdit = async (anime: Anime) => {
        try {
            setSearchAnimeLoading(true);

            // Загружаем полные данные аниме
            const response = await api.get(`/anime/${anime.id}`);
            const fullAnimeData = response.data;

            // Загружаем жанры этого конкретного аниме
            const genresResponse = await api.get(`/anime/${anime.id}/genres`);

            // Получаем только ID жанров этого аниме
            const selectedGenreIds = genresResponse.data.map((genre: any) => genre.id);

            // Загружаем студии этого аниме - используем новый endpoint
            const studiosResponse = await api.get(`/anime/${anime.id}/studios`);

            // Получаем только ID студий этого аниме
            const selectedStudioIds = studiosResponse.data.map((studio: any) => studio.id);

            // Извлекаем название папки из URL изображения
            const imageFolderValue = extractFolderFromUrl(fullAnimeData.imageUrl);

            // Заполняем форму данными аниме
            setCreateAnimeData({
                title: fullAnimeData.title,
                imageFolder: imageFolderValue,
                episodes: fullAnimeData.episodes.toString(),
                description: fullAnimeData.description || '',
                releaseDate: fullAnimeData.releaseDate ? fullAnimeData.releaseDate.toString() : '',
                animeStatus: fullAnimeData.animeStatus as AnimeStatus,
                selectedGenres: selectedGenreIds,
                selectedStudios: selectedStudioIds,
                sourceMaterial: fullAnimeData.sourceMaterial || '',
                ageRating: fullAnimeData.ageRating || ''
            });

            setSelectedAnimeForEdit(fullAnimeData);
            setSearchAnimeQuery('');
            setSearchAnimeResults([]);

        } catch (error) {
            console.error('Error loading anime data for edit:', error);
            alert('Ошибка при загрузке данных аниме');
        } finally {
            setSearchAnimeLoading(false);
        }
    };

// Вспомогательная функция для извлечения названия папки из URL
    const extractFolderFromUrl = (imageUrl: string): string => {
        if (!imageUrl) return '';

        // Пример: "/images/Аватар%20короля/poster.jpg" → "Аватар короля"
        const match = imageUrl.match(/\/images\/([^\/]+)\/poster\.(webp|jpg|jpeg|png)/i);
        if (match && match[1]) {
            try {
                return decodeURIComponent(match[1]);
            } catch {
                return match[1];
            }
        }
        return '';
    };

    const handleModeSwitch = (mode: AnimeModalMode) => {
        setAnimeModalMode(mode);

        // Сбрасываем поиск и выбранное аниме при переключении на создание
        if (mode === 'create') {
            setSelectedAnimeForEdit(null);
            setSearchAnimeQuery('');
            setSearchAnimeResults([]);

            // Сбрасываем форму только если мы переключаемся с редактирования на создание
            if (animeModalMode === 'edit') {
                setCreateAnimeData({
                    title: '',
                    imageFolder: '',
                    episodes: '',
                    description: '',
                    releaseDate: '',
                    animeStatus: AnimeStatus.ONGOING,
                    selectedGenres: [],
                    selectedStudios: [],
                    sourceMaterial: '',
                    ageRating: ''
                });
            }
        }
    };

    const handleGenreChange = (genreId: number) => {
        setCreateAnimeData(prev => {
            const isSelected = prev.selectedGenres.includes(genreId);
            const updatedGenres = isSelected
                ? prev.selectedGenres.filter(id => id !== genreId)
                : [...prev.selectedGenres, genreId];

            return {
                ...prev,
                selectedGenres: updatedGenres
            };
        });
    };

    // Функция для открытия модального окна создания аниме
    const handleOpenCreateAnime = () => {
        setShowCreateAnimeModal(true);
        setShowSettingsDropdown(false);
    };

// Функция для закрытия модального окна
    const handleCloseCreateAnime = () => {
        setShowCreateAnimeModal(false);
        setCreateAnimeData({
            title: '',
            imageFolder: '',
            episodes: '',
            description: '',
            releaseDate: '',
            animeStatus: AnimeStatus.ONGOING,
            selectedGenres: [],
            selectedStudios: [],
            sourceMaterial: '',
            ageRating: ''
        });
        setCreateAnimeErrors({});
        setAnimeModalMode('create');
        setSelectedAnimeForEdit(null);
        setSearchAnimeQuery('');
        setSearchAnimeResults([]);
    };

// Функция для обработки изменений в форме
    const handleCreateAnimeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const {name, value} = e.target;
        setCreateAnimeData(prev => ({
            ...prev,
            [name]: value
        }));
        // Очищаем ошибку для этого поля
        if (createAnimeErrors[name]) {
            setCreateAnimeErrors(prev => ({...prev, [name]: ''}));
        }
    };

// Функция для валидации формы
    const validateCreateAnimeForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!createAnimeData.title.trim()) {
            newErrors.title = 'Название обязательно';
        } else if (createAnimeData.title.length > 300) {
            newErrors.title = 'Название не может превышать 300 символов';
        }

        // ВАЛИДАЦИЯ ДЛЯ imageFolder вместо imageUrl
        if (!createAnimeData.imageFolder.trim()) {
            newErrors.imageFolder = 'Название папки с изображением обязательно';
        } else if (createAnimeData.imageFolder.length > 300) {
            newErrors.imageFolder = 'Название папки не может превышать 300 символов';
        }

        if (!createAnimeData.episodes.trim()) {
            newErrors.episodes = 'Количество эпизодов обязательно';
        } else {
            const episodes = parseInt(createAnimeData.episodes);
            if (isNaN(episodes) || episodes < 1) {
                newErrors.episodes = 'Количество эпизодов должно быть положительным числом';
            }
        }

        if (createAnimeData.description.length > 2000) {
            newErrors.description = 'Описание не может превышать 2000 символов';
        }

        if (createAnimeData.releaseDate.trim()) {
            const year = parseInt(createAnimeData.releaseDate);
            const currentYear = new Date().getFullYear();
            if (isNaN(year) || year < 1950 || year > currentYear + 2) {
                newErrors.releaseYear = 'Год выпуска должен быть между 1950 и ' + (currentYear + 2);
            }
        }

        // Валидация студий
        if (createAnimeData.selectedStudios.length === 0) {
            newErrors.studios = 'Выберите хотя бы одну студию';
        }

        // Валидация первоисточника
        if (createAnimeData.sourceMaterial && createAnimeData.sourceMaterial.length > 100) {
            newErrors.sourceMaterial = 'Первоисточник не может превышать 100 символов';
        }

        // Валидация возрастного рейтинга (опционально)
        if (createAnimeData.ageRating) {
            const validRatings = ageRatings.map(r => r.code);
            if (!validRatings.includes(createAnimeData.ageRating)) {
                newErrors.ageRating = 'Выберите корректный возрастной рейтинг';
            }
        }

        setCreateAnimeErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

// Функция для отправки формы
    const handleCreateAnimeSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setCreateAnimeErrors({});

        if (!validateCreateAnimeForm()) {
            return;
        }

        if (createAnimeData.selectedGenres.length === 0) {
            setCreateAnimeErrors(prev => ({
                ...prev,
                genres: 'Выберите хотя бы один жанр'
            }));
            return;
        }

        // Валидация студий (опционально)
        if (createAnimeData.selectedStudios.length === 0) {
            setCreateAnimeErrors(prev => ({
                ...prev,
                studios: 'Выберите хотя бы одну студию'
            }));
            return;
        }

        setCreateAnimeLoading(true);

        try {
            const animeData = {
                title: createAnimeData.title.trim(),
                episodes: parseInt(createAnimeData.episodes),
                description: createAnimeData.description.trim(),
                releaseDate: createAnimeData.releaseDate.trim() || null,
                animeStatus: createAnimeData.animeStatus,
                sourceMaterial: createAnimeData.sourceMaterial.trim() || null, // Новое поле
                ageRating: createAnimeData.ageRating || null
            };

            let response;

            if (animeModalMode === 'create') {
                response = await api.post('/admin', animeData, {
                    params: {
                        genreIds: createAnimeData.selectedGenres,
                        studioIds: createAnimeData.selectedStudios, // Новый параметр
                        imageFolder: createAnimeData.imageFolder.trim()
                    },
                    paramsSerializer: {
                        indexes: null
                    }
                });
                alert('Аниме успешно создано!');
            } else {
                // Редактирование существующего аниме
                if (!selectedAnimeForEdit) return;

                response = await api.put(`/admin/${selectedAnimeForEdit.id}`, animeData, {
                    params: {
                        genreIds: createAnimeData.selectedGenres,
                        studioIds: createAnimeData.selectedStudios,
                        imageFolder: createAnimeData.imageFolder.trim()
                    },
                    paramsSerializer: {
                        indexes: null
                    }
                });
                alert('Аниме успешно обновлено!');
            }

            handleCloseCreateAnime();

            if (response.data.id) {
                navigate(`/anime/${response.data.id}`);
            }

        } catch (error: any) {
            console.error('Create/Edit anime error:', error);
            if (error.response?.data) {
                setCreateAnimeErrors(error.response.data);
            } else {
                setCreateAnimeErrors({general: `Ошибка при ${animeModalMode === 'create' ? 'создании' : 'обновлении'} аниме. Попробуйте еще раз.`});
            }
        } finally {
            setCreateAnimeLoading(false);
        }
    };

    // Функция для создания аниме
    const handleCreateAnime = () => {
        handleOpenCreateAnime();
    };

    useEffect(() => {
        const handleBodyScroll = (shouldDisable: boolean) => {
            if (shouldDisable) {
                // Сохраняем текущую позицию скролла
                const scrollY = window.scrollY;
                document.body.style.position = 'fixed';
                document.body.style.top = `-${scrollY}px`;
                document.body.style.width = '100%';
                document.body.style.overflow = 'hidden';
            } else {
                // Восстанавливаем скролл
                const scrollY = document.body.style.top;
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.width = '';
                document.body.style.overflow = '';
                window.scrollTo(0, parseInt(scrollY || '0') * -1);
            }
        };

        if (showSearchModal || showAuthModal || showCreateAnimeModal) {
            handleBodyScroll(true);
        } else {
            handleBodyScroll(false);
        }

        return () => {
            // Cleanup при размонтировании
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.overflow = '';
        };
    }, [showSearchModal, showAuthModal, showCreateAnimeModal]);

    const fetchSearchResults = useCallback(async (query: string) => {
        const trimmedQuery = query.trim();

        if (!trimmedQuery) {
            setSearchResults([]);
            setShowNoResults(false);
            setShowMinCharsMessage(false);
            return;
        }

        // Проверка минимальной длины
        if (trimmedQuery.length < 2) {
            setSearchResults([]);
            setShowNoResults(false);
            setShowMinCharsMessage(true);
            return;
        }

        setShowMinCharsMessage(false);

        try {
            const response = await api.get(`/anime/search?title=${encodeURIComponent(trimmedQuery)}&page=0&size=6`);
            let results = response.data;
            if (response.data && response.data.content) {
                results = response.data.content;
            }

            // Сохраняем полное количество результатов для кнопки "Показать все"
            const totalResults = response.data?.totalElements || results.length;
            setTotalAnimeResults(totalResults);

            setSearchResults(results);

            if (results.length === 0) {
                setShowNoResults(true);
            } else {
                setShowNoResults(false);
            }
        } catch (error: any) {
            console.error('Search error:', error);
            setSearchResults([]);
            setShowNoResults(true);
            setShowMinCharsMessage(false);
        }
    }, []);

    useEffect(() => {
        if (debouncedSearchQuery.trim() && showSearchModal) {
            if (searchMode === 'anime') {
                fetchSearchResults(debouncedSearchQuery);
            } else {
                fetchUserSearchResults(debouncedSearchQuery);
            }
        } else if (!debouncedSearchQuery.trim() && showSearchModal) {
            if (searchMode === 'anime') {
                setSearchResults([]);
                setShowNoResults(false);
                setShowMinCharsMessage(false);
                setTotalAnimeResults(0);
            } else {
                setUserSearchResults([]);
                setShowNoUserResults(false);
                setShowMinCharsMessage(false);
            }
        }
    }, [debouncedSearchQuery, showSearchModal, searchMode, fetchSearchResults, fetchUserSearchResults]);

    // Обработчик для выбора студий (аналогично жанрам)
    const handleStudioChange = (studioId: number) => {
        setCreateAnimeData(prev => {
            const isSelected = prev.selectedStudios.includes(studioId);
            const updatedStudios = isSelected
                ? prev.selectedStudios.filter(id => id !== studioId)
                : [...prev.selectedStudios, studioId];

            return {
                ...prev,
                selectedStudios: updatedStudios
            };
        });
    };

    // Обработчик для первоисточника
    const handleSourceMaterialChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;
        setCreateAnimeData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        setSearchQuery(query);
    };

    const handleShowAllAnimeResults = () => {
        if (!searchQuery.trim() || searchMode !== 'anime') return;

        // Закрываем модальное окно поиска
        setShowSearchModal(false);
        setSearchQuery('');
        setSearchResults([]);

        // Переходим на страницу списка аниме с примененным фильтром
        navigate(`/anime?title=${encodeURIComponent(searchQuery.trim())}`);
    };

    const checkUsernameAvailability = async (username: string) => {
        if (username.length < 4) return;

        try {
            const response = await api.get(`/auth/check-username?username=${encodeURIComponent(username)}`);
            return response.data;
        } catch (error) {
            console.error('Error checking username:', error);
            return null;
        }
    };

// Функция для проверки доступности никнейма (убираем спиннер)
    const checkNicknameAvailability = async (nickname: string) => {
        if (nickname.length < 4) return;

        try {
            const response = await api.get(`/auth/check-nickname?nickname=${encodeURIComponent(nickname)}`);
            return response.data;
        } catch (error) {
            console.error('Error checking nickname:', error);
            return null;
        }
    };

    const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '');
        setRegisterData(prev => ({...prev, username: value}));

        // Сбрасываем результат
        setUsernameAvailable(null);

        // Сбрасываем ошибки
        if (registerErrors.username) {
            setRegisterErrors(prev => ({...prev, username: ''}));
        }

        // Отменяем предыдущий таймер
        if (usernameCheckTimeout.current) {
            clearTimeout(usernameCheckTimeout.current);
        }

        // Проверяем доступность только если длина >= 4
        if (value.length >= 4) {
            // Устанавливаем состояние проверки сразу при начале отсчета дебаунса
            // Это покажет "Проверка..." пока ждем 500ms перед запросом
            setCheckingUsernameAvailability(true);

            usernameCheckTimeout.current = setTimeout(async () => {
                try {
                    const result = await checkUsernameAvailability(value);

                    if (result && !result.available) {
                        setUsernameAvailable(false);
                        setRegisterErrors(prev => ({
                            ...prev,
                            username: 'Этот логин уже занят'
                        }));
                    } else if (result && result.available) {
                        setUsernameAvailable(true);
                        // Очищаем ошибку, если логин доступен
                        if (registerErrors.username && registerErrors.username.includes('занят')) {
                            setRegisterErrors(prev => ({...prev, username: ''}));
                        }
                    }
                } catch (error) {
                    setUsernameAvailable(null);
                } finally {
                    // В любом случае сбрасываем состояние проверки
                    setCheckingUsernameAvailability(false);
                }
            }, 500);
        } else {
            // Если меньше 4 символов, сбрасываем все состояния
            setCheckingUsernameAvailability(false);
            setUsernameAvailable(null);
        }
    };

    const handleNicknameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '');
        setRegisterData(prev => ({...prev, nickname: value}));

        setNicknameAvailable(null);

        if (registerErrors.nickname) {
            setRegisterErrors(prev => ({...prev, nickname: ''}));
        }

        if (nicknameCheckTimeout.current) {
            clearTimeout(nicknameCheckTimeout.current);
        }

        if (value.length >= 4) {
            setCheckingNicknameAvailability(true);

            nicknameCheckTimeout.current = setTimeout(async () => {
                try {
                    const result = await checkNicknameAvailability(value);

                    if (result && !result.available) {
                        setNicknameAvailable(false);
                        setRegisterErrors(prev => ({
                            ...prev,
                            nickname: 'Этот никнейм уже занят'
                        }));
                    } else if (result && result.available) {
                        setNicknameAvailable(true);
                        if (registerErrors.nickname && registerErrors.nickname.includes('занят')) {
                            setRegisterErrors(prev => ({...prev, nickname: ''}));
                        }
                    }
                } catch (error) {
                    setNicknameAvailable(null);
                } finally {
                    setCheckingNicknameAvailability(false);
                }
            }, 500);
        } else {
            setCheckingNicknameAvailability(false);
            setNicknameAvailable(null);
        }
    };

    // Функции для модального окна авторизации
    const validateLoginForm = () => {
        const newErrors: { [key: string]: string } = {};
        if (!loginData.username.trim()) newErrors.username = 'Логин обязателен';
        if (!loginData.password.trim()) newErrors.password = 'Пароль обязателен';
        setLoginErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateRegisterForm = () => {
        const newErrors: { [key: string]: string } = {};

        // Валидация логина
        if (registerData.username.length < 4 || registerData.username.length > 15) {
            newErrors.username = 'Логин должен содержать от 4 до 15 символов';
        } else if (!/^[a-zA-Z0-9_-]+$/.test(registerData.username)) {
            newErrors.username = 'Логин может содержать только буквы, цифры, дефис и подчеркивание';
        }

        if (!registerData.nickname.trim()) {
            newErrors.nickname = 'Никнейм обязателен';
        } else if (registerData.nickname.length < 4 || registerData.nickname.length > 20) {
            newErrors.nickname = 'Никнейм должен содержать от 4 до 20 символов';
        } else if (!/^[a-zA-Z0-9_-]+$/.test(registerData.nickname)) {
            newErrors.nickname = 'Никнейм может содержать только буквы, цифры, дефис и подчеркивание';
        }

        // Валидация пароля
        if (registerData.password.length < 6 || registerData.password.length > 50) {
            newErrors.password = 'Пароль должен содержать от 6 до 50 символов';
        } else if (/\s/.test(registerData.password)) {
            newErrors.password = 'Пароль не может содержать пробелы';
        }

        // Подтверждение пароля
        if (registerData.password !== registerData.confirmPassword) {
            newErrors.confirmPassword = 'Пароли не совпадают';
        }

        setRegisterErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleLoginSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoginErrors({});

        if (!validateLoginForm()) {
            return;
        }

        try {
            const response = await api.post('/auth/login', {
                username: loginData.username,
                password: loginData.password,
                rememberMe: loginData.rememberMe // Добавить параметр
            });
            if (response.data.token) {
                login(response.data.token, loginData.rememberMe); // Передаем rememberMe
                setShowAuthModal(false);
                setLoginData({username: '', password: '', rememberMe: false}); // Сбросить rememberMe тоже
                setShowLoginPassword(false);
            }
        } catch (err: any) {
            console.error('Login error:', err);
            if (err.response?.data) {
                const errorData = err.response.data;

                if (typeof errorData === 'string') {
                    if (errorData.includes('неверн') || errorData.includes('invalid')) {
                        setLoginErrors({general: 'Неверный логин или пароль'});
                    } else {
                        setLoginErrors({general: errorData});
                    }
                } else if (typeof errorData === 'object') {
                    if (errorData.message) {
                        setLoginErrors({general: errorData.message});
                    } else {
                        setLoginErrors({general: 'Ошибка входа. Попробуйте еще раз.'});
                    }
                }
            } else {
                setLoginErrors({general: 'Ошибка входа. Проверьте подключение.'});
            }
        }
    };

    const handleRegisterSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setRegisterErrors({});
        setRegisterError(null);

        if (!validateRegisterForm()) {
            return;
        }

        try {
            await api.post('/auth/register', {
                username: registerData.username,
                nickname: registerData.nickname,
                password: registerData.password
            });
            alert('Регистрация успешна!');
            setAuthMode('login');
            setRegisterData({
                username: '',
                nickname: '',
                password: '',
                confirmPassword: ''
            });
        } catch (err: any) {
            console.error('Registration error:', err);

            // Обработка ошибок с бекенда
            if (err.response?.data) {
                const errorData = err.response.data;

                // Проверяем, содержит ли ошибка сообщения о занятых данных
                // НЕ добавляем ошибки о занятости - только другие ошибки
                if (typeof errorData === 'string') {
                    // Если это не ошибка занятости
                    if (!errorData.toLowerCase().includes('username') &&
                        !errorData.toLowerCase().includes('nickname') &&
                        !errorData.toLowerCase().includes('логин') &&
                        !errorData.toLowerCase().includes('никнейм') &&
                        !errorData.toLowerCase().includes('занят')) {
                        setRegisterError(errorData);
                    }
                } else if (typeof errorData === 'object') {
                    // Если сервер возвращает объект с ошибками
                    const newErrors: { [key: string]: string } = {};

                    // Добавляем только ошибки НЕ связанные с занятостью
                    if (errorData.username && !errorData.username.includes('уже') && !errorData.username.includes('занят')) {
                        newErrors.username = errorData.username;
                    }
                    if (errorData.nickname && !errorData.nickname.includes('уже') && !errorData.nickname.includes('занят')) {
                        newErrors.nickname = errorData.nickname;
                    }
                    if (errorData.password) {
                        newErrors.password = errorData.password;
                    }

                    if (Object.keys(newErrors).length > 0) {
                        setRegisterErrors(newErrors);
                    } else if (errorData.message &&
                        !errorData.message.includes('уже') &&
                        !errorData.message.includes('занят')) {
                        setRegisterError(errorData.message);
                    }
                }
            } else if (err.request) {
                setRegisterError('Нет ответа от сервера. Проверьте подключение.');
            } else {
                setRegisterError('Ошибка регистрации. Попробуйте еще раз.');
            }
        }
    };

    const handleLoginInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value, type, checked} = e.target;

        setLoginData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (loginErrors[name]) {
            setLoginErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleRegisterInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;
        setRegisterData(prev => ({
            ...prev,
            [name]: value
        }));
        if (registerErrors[name]) {
            setRegisterErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const switchToRegister = () => {
        setAuthMode('register');
        setLoginErrors({});
        setRegisterErrors({});
        setRegisterError(null);
    };

    const switchToLogin = () => {
        setAuthMode('login');
        setLoginData({username: '', password: '', rememberMe: false});
        setLoginErrors({});
        setRegisterErrors({});
        setRegisterError(null);
    };

    const handleAuthModalClose = () => {
        setShowAuthModal(false);
        setAuthMode('login');
        setLoginData({username: '', password: '', rememberMe: false});
        setRegisterData({
            username: '',
            nickname: '',
            password: '',
            confirmPassword: ''
        });
        setLoginErrors({});
        setRegisterErrors({});
        setRegisterError(null);
        setShowLoginPassword(false);
        setShowRegisterPassword(false);
        setShowRegisterConfirmPassword(false);
        // Сбрасываем состояния подсказок
        setShowHelp({
            username: false,
            nickname: false,
            password: false,
            confirmPassword: false
        });
        // Сбрасываем состояния проверки доступности
        setCheckingUsernameAvailability(false);
        setCheckingNicknameAvailability(false);
        setUsernameAvailable(null);
        setNicknameAvailable(null);
    };

    const handleSearchModalClose = () => {
        setShowSearchModal(false);
        setSearchQuery('');
        setSearchResults([]);
        setUserSearchResults([]);
        setShowNoResults(false);
        setShowNoUserResults(false);
        setSearchMode('anime');
    };

    useEffect(() => {
        if (showSearchModal && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [showSearchModal]);

    // Закрытие модальных окон при клике вне их
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (ageRatingDropdownRef.current &&
                !ageRatingDropdownRef.current.contains(event.target as Node) &&
                showAgeRatingDropdown) {
                setShowAgeRatingDropdown(false);
            }
            if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
                setShowSettingsDropdown(false);
            }
            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                if (showAuthModal) {
                    handleAuthModalClose();
                }
                if (showCreateAnimeModal) {
                    handleCloseCreateAnime();
                }
            }
            if (searchModalRef.current && !searchModalRef.current.contains(event.target as Node) && showSearchModal) {
                handleSearchModalClose();
            }
            if (notificationsRef.current &&
                !notificationsRef.current.contains(event.target as Node) &&
                showNotifications) {
                setShowNotifications(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showAuthModal, showSearchModal, showCreateAnimeModal, showNotifications, showAgeRatingDropdown]);

    useEffect(() => {
        const handleOpenAuthModal = (event: CustomEvent) => {
            const {mode} = event.detail;
            setShowAuthModal(true);
            setAuthMode(mode);
        };

        window.addEventListener('openAuthModal', handleOpenAuthModal as EventListener);

        return () => {
            window.removeEventListener('openAuthModal', handleOpenAuthModal as EventListener);
        };
    }, []);

    return (
        <>
            <header className="anime-header">
                <div className="header-content">
                    <h1
                        className="header-logo"
                        {...makeClickable('/')}
                        style={{cursor: 'pointer'}}
                    >
                        AnimeList
                    </h1>

                    <nav className="header-nav">
                        {/* Кнопка поиска */}
                        <button
                            className="search-icon-button"
                            onClick={() => setShowSearchModal(true)}
                            title="Search"
                        >
                            <Search className="search-icon" size={22}/>
                        </button>

                        {isAuthenticated && (
                            <>
                                {/* Кнопка уведомлений */}
                                <div className="notifications-container" ref={notificationsRef}>
                                    <button
                                        className="notifications-icon-button"
                                        onClick={() => {
                                            setShowNotifications(!showNotifications);
                                            if (!showNotifications) {
                                                setNotificationTab('unread');
                                                fetchNotifications();
                                            }
                                        }}
                                        title="Уведомления"
                                    >
                                        <Bell className="notifications-icon" size={22}/>
                                        {unreadCount > 0 && (
                                            <span className="notification-badge">
                                                {unreadCount > 10 ? '10+' : unreadCount}
                                            </span>
                                        )}
                                    </button>

                                    {showNotifications && (
                                        <>
                                            <div className="dropdown-overlay"
                                                 onClick={() => setShowNotifications(false)}/>
                                            <div className="notifications-dropdown">
                                                <div className="notifications-header">
                                                    <h4 className="notifications-title">Уведомления</h4>
                                                    {unreadCount > 0 && (
                                                        <button
                                                            className="mark-all-read-btn"
                                                            onClick={markAllAsRead}
                                                            disabled={unreadCount === 0}
                                                        >
                                                            Прочитать все
                                                        </button>
                                                    )}
                                                </div>

                                                {/* ВКЛАДКИ */}
                                                <div className="notifications-tabs">
                                                    <button
                                                        className={`notification-tab ${notificationTab === 'unread' ? 'active' : ''}`}
                                                        onClick={() => setNotificationTab('unread')}
                                                    >
                                                        Непрочитанные
                                                    </button>
                                                    <button
                                                        className={`notification-tab ${notificationTab === 'read' ? 'active' : ''}`}
                                                        onClick={() => setNotificationTab('read')}
                                                    >
                                                        Прочитанные
                                                    </button>
                                                </div>

                                                <div className="notifications-list">
                                                    {notificationsLoading ? (
                                                        <div className="notifications-loading">
                                                            <div className="header-loading-spinner-tiny"></div>
                                                            <span>Загрузка...</span>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            {/* Непрочитанные уведомления */}
                                                            {notificationTab === 'unread' && (
                                                                notifications.filter((n: any) => !n.read).length > 0 ? (
                                                                    notifications
                                                                        .filter((n: any) => !n.read)
                                                                        .slice(0, 30) // Лимит 30
                                                                        .map((notification) => (
                                                                            <NotificationItem
                                                                                key={notification.id}
                                                                                notification={notification}
                                                                                onNotificationClick={handleNotificationClick}
                                                                                onDelete={deleteNotification}
                                                                                formatNotificationTime={formatNotificationTime}
                                                                                getNotificationIcon={getNotificationIcon}
                                                                                getNotificationText={getNotificationText}
                                                                            />
                                                                        ))
                                                                ) : (
                                                                    <div className="no-notifications">
                                                                        <Bell size={32}/>
                                                                        <p>Нет уведомлений</p>
                                                                    </div>
                                                                )
                                                            )}

                                                            {/* Прочитанные уведомления */}
                                                            {notificationTab === 'read' && (
                                                                notifications.filter((n: any) => n.read).length > 0 ? (
                                                                    notifications
                                                                        .filter((n: any) => n.read)
                                                                        .slice(0, 30) // Лимит 30
                                                                        .map((notification) => (
                                                                            <NotificationItem
                                                                                key={notification.id}
                                                                                notification={notification}
                                                                                onNotificationClick={handleNotificationClick}
                                                                                onDelete={deleteNotification}
                                                                                formatNotificationTime={formatNotificationTime}
                                                                                getNotificationIcon={getNotificationIcon}
                                                                                getNotificationText={getNotificationText}
                                                                            />
                                                                        ))
                                                                ) : (
                                                                    <div className="no-notifications">
                                                                        <Bell size={32}/>
                                                                        <p>Нет уведомлений</p>
                                                                    </div>
                                                                )
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </>
                        )}

                        {isAuthenticated ? (
                            <>
                                <button
                                    className="profile-icon-button"
                                    {...makeClickable(
                                        '/profile',
                                        (e) => {
                                            e.preventDefault();
                                            handleProfileClick();
                                        }
                                    )}
                                >
                                    <User className="profile-icon" size={22}/>
                                </button>

                                <div className="settings-container" ref={settingsRef}>
                                    <button
                                        className="settings-icon-button"
                                        onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
                                        title="Settings"
                                    >
                                        <Settings className="settings-icon" size={22}/>
                                    </button>

                                    {showSettingsDropdown && (
                                        <>
                                            <div className="dropdown-overlay"
                                                 onClick={() => setShowSettingsDropdown(false)}/>
                                            <div className="settings-dropdown">
                                                <button
                                                    className={`settings-dropdown-item theme-toggle-item ${theme}`}
                                                    onClick={() => {
                                                        toggleTheme();
                                                    }}
                                                >
                                                    <div className="theme-toggle-icon-wrapper">
                                                        {theme === 'light' ? (
                                                            <Moon size={18} className="theme-icon"/>
                                                        ) : (
                                                            <Sun size={18} className="theme-icon"/>
                                                        )}
                                                    </div>
                                                    <span className="theme-toggle-text">
                                                        {theme === 'light' ? 'Тёмная тема' : 'Светлая тема'}
                                                    </span>
                                                </button>

                                                {isAdmin && (
                                                    <button
                                                        className="settings-dropdown-item"
                                                        onClick={handleCreateAnime}
                                                    >
                                                        <svg className="settings-dropdown-icon" viewBox="0 0 24 24"
                                                             fill="currentColor">
                                                            <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"/>
                                                        </svg>
                                                        Создать/Редактировать
                                                    </button>
                                                )}

                                                {/* Здесь можно добавить другие пункты меню в будущем */}
                                                <button className="settings-dropdown-item" disabled>
                                                    <svg className="settings-dropdown-icon" viewBox="0 0 24 24"
                                                         fill="currentColor">
                                                        <path
                                                            d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.21,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.21,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.67 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"/>
                                                    </svg>
                                                    Настройки (скоро)
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>

                                <button onClick={logout} className="logout-button" title="Logout">
                                    <LogOut className="logout-icon" size={22}/>
                                </button>
                            </>
                        ) : (
                            <>
                                <div className="settings-container" ref={settingsRef}>
                                    <button
                                        className="settings-icon-button"
                                        onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
                                        title="Settings"
                                    >
                                        <Settings className="settings-icon" size={22}/>
                                    </button>

                                    {showSettingsDropdown && (
                                        <>
                                            <div className="dropdown-overlay"
                                                 onClick={() => setShowSettingsDropdown(false)}/>
                                            <div className="settings-dropdown">
                                                <button
                                                    className={`settings-dropdown-item theme-toggle-item ${theme}`}
                                                    onClick={() => {
                                                        toggleTheme();
                                                    }}
                                                >
                                                    <div className="theme-toggle-icon-wrapper">
                                                        {theme === 'light' ? (
                                                            <Moon size={18} className="theme-icon"/>
                                                        ) : (
                                                            <Sun size={18} className="theme-icon"/>
                                                        )}
                                                    </div>
                                                    <span className="theme-toggle-text">
                                                        {theme === 'light' ? 'Тёмная тема' : 'Светлая тема'}
                                                    </span>
                                                </button>

                                                {/* Здесь можно добавить другие пункты меню в будущем */}
                                                <button className="settings-dropdown-item" disabled>
                                                    <svg className="settings-dropdown-icon" viewBox="0 0 24 24"
                                                         fill="currentColor">
                                                        <path
                                                            d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.21,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.21,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.67 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"/>
                                                    </svg>
                                                    Настройки (скоро)
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>

                                <button
                                    className="login-icon-button"
                                    onClick={() => {
                                        setShowAuthModal(true);
                                        setAuthMode('login');
                                    }}
                                    title="Login"
                                >
                                    <LogIn className="login-icon" size={22}/>
                                </button>
                            </>
                        )}
                    </nav>
                </div>
            </header>

            {showSearchModal && (
                <div className="modal-overlay">
                    <div className="modal-content search-modal" ref={searchModalRef}>
                        <div className="search-header">
                            <h3>Поиск</h3>
                            <button
                                className="search-close"
                                onClick={handleSearchModalClose}
                            >
                                <X size={30}/>
                            </button>
                        </div>
                        <div className="search-header-row">
                            <div className="search-modal-form">
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    placeholder={
                                        searchMode === 'anime'
                                            ? "Введите название аниме..."
                                            : "Введите никнейм пользователя..."
                                    }
                                    className="modal-input search-modal-input"
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    autoFocus
                                />
                            </div>

                            <div className="search-mode-switcher">
                                <button
                                    className={`search-mode-btn ${searchMode === 'anime' ? 'active' : ''}`}
                                    onClick={() => setSearchMode('anime')}
                                >
                                    Аниме
                                </button>
                                <button
                                    className={`search-mode-btn ${searchMode === 'users' ? 'active' : ''}`}
                                    onClick={() => setSearchMode('users')}
                                >
                                    Люди
                                </button>
                            </div>
                        </div>

                        {/* Результаты поиска */}
                        <div className="search-modal-results">
                            {searchMode === 'anime' ? (
                                <>
                                    {showMinCharsMessage ? (
                                        <div className="search-min-chars-message">
                                            <Search className="search-min-chars-icon" size={200}/>
                                            <div className="search-min-chars-text">
                                                Введите минимум 2 символа для поиска
                                            </div>
                                        </div>
                                    ) : showNoResults ? (
                                        <div className="search-no-results">
                                            <div className="search-no-results-text">
                                                Нет аниме по запросу "{searchQuery}"
                                            </div>
                                        </div>
                                    ) : searchResults.length > 0 ? (
                                        <>
                                            <div className="search-results-list">
                                                {/* Левая колонка - элементы с четными индексами (0, 2, 4) */}
                                                <div className="search-results-column">
                                                    {searchResults.slice(0, 6).map((anime, index) => {
                                                        if (index % 2 === 0) {
                                                            return (
                                                                <div
                                                                    key={anime.id}
                                                                    className="search-result-item"
                                                                    {...makeClickable(`/anime/${anime.id}`,
                                                                        () => setShowSearchModal(false))}
                                                                >
                                                                    <img
                                                                        src={anime.imageUrl}
                                                                        alt={anime.title}
                                                                        className="search-result-image"
                                                                        onError={(e) => {
                                                                            e.currentTarget.src = "https://images.unsplash.com/photo-1578632767115-351597cf2477?w=50&h=70&fit=crop";
                                                                        }}
                                                                    />
                                                                    <div className="search-result-info">
                                                                        <span className="search-result-title">{anime.title}</span>
                                                                        <span className="search-result-episodes">EP {anime.episodes}</span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }
                                                        return null;
                                                    })}
                                                </div>

                                                {/* Правая колонка - элементы с нечетными индексами (1, 3, 5) */}
                                                <div className="search-results-column">
                                                    {searchResults.slice(0, 6).map((anime, index) => {
                                                        if (index % 2 === 1) {
                                                            return (
                                                                <div
                                                                    key={anime.id}
                                                                    className="search-result-item"
                                                                    {...makeClickable(`/anime/${anime.id}`,
                                                                        () => setShowSearchModal(false))}
                                                                >
                                                                    <img
                                                                        src={anime.imageUrl}
                                                                        alt={anime.title}
                                                                        className="search-result-image"
                                                                        onError={(e) => {
                                                                            e.currentTarget.src = "https://images.unsplash.com/photo-1578632767115-351597cf2477?w=50&h=70&fit=crop";
                                                                        }}
                                                                    />
                                                                    <div className="search-result-info">
                                                                        <span className="search-result-title">{anime.title}</span>
                                                                        <span className="search-result-episodes">EP {anime.episodes}</span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }
                                                        return null;
                                                    })}
                                                </div>
                                            </div>

                                            {/* Кнопка "Показать все" если результатов больше 6 */}
                                            {totalAnimeResults > 6 && (
                                                <div className="search-show-all-container">
                                                    <div
                                                        className="search-show-all-btn"
                                                        {...makeClickable(
                                                            `/anime?title=${encodeURIComponent(searchQuery.trim())}`,
                                                            (e) => {
                                                                e.stopPropagation();
                                                                handleShowAllAnimeResults();
                                                            }
                                                        )}
                                                        style={{cursor: 'pointer'}}
                                                    >
                                                        Показать все результаты ({totalAnimeResults})
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="search-empty-state">
                                            <Search className="search-empty-icon" size={200}/>
                                            <div className="search-empty-text">Введите название аниме</div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                // Для поиска пользователей
                                <>
                                    {showMinCharsMessage ? (
                                        <div className="search-min-chars-message">
                                            <User className="search-min-chars-icon" size={200}/>
                                            <div className="search-min-chars-text">
                                                Введите минимум 2 символа для поиска
                                            </div>
                                        </div>
                                    ) : showNoUserResults ? (
                                        <div className="search-no-results">
                                            <div className="search-no-results-text">
                                                Нет пользователей по запросу "{searchQuery}"
                                            </div>
                                        </div>
                                    ) : userSearchResults.length > 0 ? (
                                        <>
                                            <div className="search-results-list">
                                                {/* Левая колонка - элементы с четными индексами (0, 2, 4) */}
                                                <div className="search-results-column">
                                                    {userSearchResults.slice(0, 6).map((user, index) => {
                                                        if (index % 2 === 0) {
                                                            return (
                                                                <div
                                                                    key={user.id}
                                                                    className="search-result-item"
                                                                    {...makeClickable(`/users/${user.nickname}`,
                                                                        () => setShowSearchModal(false))}
                                                                >
                                                                    <img
                                                                        src={user.avatarUrl || "/user-images/default/avatar.jpg"}
                                                                        alt={user.nickname}
                                                                        className="search-result-image user-avatar"
                                                                        onError={(e) => {
                                                                            e.currentTarget.src = "/user-images/default/avatar.jpg";
                                                                        }}
                                                                    />
                                                                    <div className="search-result-info">
                                                                        <span className="search-result-title">{user.nickname}</span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }
                                                        return null;
                                                    })}
                                                </div>

                                                {/* Правая колонка - элементы с нечетными индексами (1, 3, 5) */}
                                                <div className="search-results-column">
                                                    {userSearchResults.slice(0, 6).map((user, index) => {
                                                        if (index % 2 === 1) {
                                                            return (
                                                                <div
                                                                    key={user.id}
                                                                    className="search-result-item"
                                                                    {...makeClickable(`/users/${user.nickname}`,
                                                                        () => setShowSearchModal(false))}
                                                                >
                                                                    <img
                                                                        src={user.avatarUrl || "/user-images/default/avatar.jpg"}
                                                                        alt={user.nickname}
                                                                        className="search-result-image user-avatar"
                                                                        onError={(e) => {
                                                                            e.currentTarget.src = "/user-images/default/avatar.jpg";
                                                                        }}
                                                                    />
                                                                    <div className="search-result-info">
                                                                        <span className="search-result-title">{user.nickname}</span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }
                                                        return null;
                                                    })}
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="search-empty-state">
                                            <User className="search-empty-icon" size={200}/>
                                            <div className="search-empty-text">Введите никнейм пользователя</div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showAuthModal && (
                <div className="modal-overlay">
                    <div className="modal-content auth-modal" ref={modalRef}>
                        {authMode === 'login' ? (
                            <>
                                <h3>Вход в аккаунт</h3>
                                <form onSubmit={handleLoginSubmit} className="auth-form">
                                    <div className="form-input-container">
                                        <input
                                            type="text"
                                            name="username"
                                            placeholder="Логин"
                                            value={loginData.username}
                                            onChange={handleLoginInputChange}
                                            className={`modal-input ${loginErrors.username ? 'error' : ''}`}
                                            required
                                        />
                                        {loginErrors.username &&
                                            <p className="input-error">{loginErrors.username}</p>}
                                    </div>

                                    <div className="form-input-container password-container">
                                        <input
                                            type={showLoginPassword ? "text" : "password"}
                                            name="password"
                                            placeholder="Пароль"
                                            value={loginData.password}
                                            onChange={handleLoginInputChange}
                                            className={`modal-input ${loginErrors.password ? 'error' : ''}`}
                                            required
                                        />
                                        {loginData.password.length > 0 && (
                                            <button
                                                type="button"
                                                className="password-toggle-btn"
                                                onClick={() => setShowLoginPassword(!showLoginPassword)}
                                                tabIndex={-1}
                                            >
                                                {showLoginPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
                                            </button>
                                        )}
                                        {loginErrors.password &&
                                            <p className="input-error">{loginErrors.password}</p>}
                                    </div>

                                    <div className="remember-me-container">
                                        <div style={{display: 'flex', alignItems: 'center'}}>
                                            <input
                                                type="checkbox"
                                                id="rememberMeCheckbox"
                                                name="rememberMe"
                                                checked={loginData.rememberMe}
                                                onChange={handleLoginInputChange}
                                                className="remember-me-checkbox"
                                                style={{cursor: 'pointer'}}
                                            />
                                            {/* Отдельный элемент для текста, не связанный с input */}
                                            <div
                                                className="remember-me-text"
                                                style={{
                                                    cursor: 'default',
                                                    userSelect: 'none'
                                                }}
                                                // Обработчик клика по тексту - ничего не делает
                                                onClick={(e) => e.preventDefault()}
                                            >
                                                Запомнить меня
                                            </div>
                                        </div>
                                    </div>

                                    {loginErrors.general &&
                                        <p className="input-error general-error">{loginErrors.general}</p>}

                                    <div className="modal-actions">
                                        <button type="submit" className="modal-submit-btn">
                                            Войти
                                        </button>
                                    </div>

                                    <div className="auth-switch-container">
                                        <span>Нет аккаунта? </span>
                                        <button
                                            type="button"
                                            className="auth-switch-link"
                                            onClick={switchToRegister}
                                        >
                                            Зарегистрироваться
                                        </button>
                                    </div>
                                </form>
                            </>
                        ) : (
                            <>
                                <h3>Создание аккаунта</h3>
                                <form onSubmit={handleRegisterSubmit} className="auth-form">
                                    {/* Логин */}
                                    <div className="form-input-container">
                                        <div className="input-with-help">
                                            <input
                                                type="text"
                                                name="username"
                                                placeholder="Логин"
                                                value={registerData.username}
                                                onChange={handleUsernameChange}
                                                className={`modal-input ${registerErrors.username ? 'error' : ''}`}
                                                required
                                                minLength={4}
                                                maxLength={15}
                                                pattern="[a-z0-9_-]+"
                                                onFocus={() => {
                                                    setShowHelp(prev => ({...prev, username: false})); // Прячем подсказку
                                                }}
                                            />
                                            {/* Кнопка помощи - скрывается при фокусе и когда есть символы */}
                                            {registerData.username.length === 0 && (
                                                <button
                                                    type="button"
                                                    className="help-btn"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setShowHelp(prev => ({...prev, username: !prev.username}));
                                                    }}
                                                    tabIndex={-1}
                                                >
                                                    <HelpCircle size={16}/>
                                                </button>
                                            )}

                                            {/* Индикатор доступности */}
                                            {registerData.username.length >= 4 && (
                                                <div className="availability-indicator">
                                                    {checkingUsernameAvailability ? (
                                                        <span className="availability-checking">Проверка...</span>
                                                    ) : usernameAvailable === false ? (
                                                        <span className="availability-error">Занят</span>
                                                    ) : usernameAvailable === true ? (
                                                        <span className="availability-success">Доступен</span>
                                                    ) : null}
                                                </div>
                                            )}
                                        </div>

                                        {/* Подсказка */}
                                        {showHelp.username && (
                                            <div className="help-tooltip">
                                                Логин должен содержать:
                                                <ul>
                                                    <li>От 4 до 15 символов</li>
                                                    <li>Только латинские буквы (a-z)</li>
                                                    <li>Цифры (0-9)</li>
                                                    <li>Дефис (-) или подчеркивание (_)</li>
                                                    <li>Без пробелов и спецсимволов</li>
                                                    <li>Пример: user_123 или user-name</li>
                                                </ul>
                                            </div>
                                        )}

                                        {registerErrors.username && !registerErrors.username.includes('занят') &&
                                            <p className="input-error">{registerErrors.username}</p>}
                                    </div>

                                    {/* Никнейм */}
                                    <div className="form-input-container">
                                        <div className="input-with-help">
                                            <input
                                                type="text"
                                                name="nickname"
                                                placeholder="Никнейм"
                                                value={registerData.nickname}
                                                onChange={handleNicknameChange}
                                                className={`modal-input ${registerErrors.nickname ? 'error' : ''}`}
                                                required
                                                minLength={4}
                                                maxLength={20}
                                                pattern="[a-z0-9_-]+"
                                                onFocus={() => {
                                                    setShowHelp(prev => ({...prev, nickname: false}));
                                                }}
                                            />
                                            {/* Кнопка помощи - скрывается когда есть символы */}
                                            {registerData.nickname.length === 0 && (
                                                <button
                                                    type="button"
                                                    className="help-btn"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setShowHelp(prev => ({...prev, nickname: !prev.nickname}));
                                                    }}
                                                    tabIndex={-1}
                                                >
                                                    <HelpCircle size={16}/>
                                                </button>
                                            )}

                                            {registerData.nickname.length >= 4 && (
                                                <div className="availability-indicator">
                                                    {checkingNicknameAvailability ? (
                                                        <span className="availability-checking">Проверка...</span>
                                                    ) : nicknameAvailable === false ? (
                                                        <span className="availability-error">Занят</span>
                                                    ) : nicknameAvailable === true ? (
                                                        <span className="availability-success">Доступен</span>
                                                    ) : null}
                                                </div>
                                            )}
                                        </div>

                                        {showHelp.nickname && (
                                            <div className="help-tooltip">
                                                Никнейм должен содержать:
                                                <ul>
                                                    <li>От 4 до 20 символов</li>
                                                    <li>Только латинские буквы (a-z)</li>
                                                    <li>Цифры (0-9)</li>
                                                    <li>Дефис (-) или подчеркивание (_)</li>
                                                    <li>Без пробелов и спецсимволов</li>
                                                    <li>Будет отображаться другим пользователям</li>
                                                </ul>
                                            </div>
                                        )}

                                        {registerErrors.nickname && !registerErrors.nickname.includes('занят') &&
                                            <p className="input-error">{registerErrors.nickname}</p>}
                                    </div>

                                    {/* Пароль */}
                                    <div className="form-input-container password-container">
                                        <div className="input-with-help">
                                            <input
                                                type={showRegisterPassword ? "text" : "password"}
                                                name="password"
                                                placeholder="Пароль"
                                                value={registerData.password}
                                                onChange={(e) => {
                                                    setRegisterData(prev => ({...prev, password: e.target.value}));
                                                    if (registerErrors.password) {
                                                        setRegisterErrors(prev => ({...prev, password: ''}));
                                                    }
                                                }}
                                                className={`modal-input ${registerErrors.password ? 'error' : ''}`}
                                                required
                                                minLength={6}
                                                maxLength={50}
                                                onFocus={() => {
                                                    setShowHelp(prev => ({...prev, password: false}));
                                                }}
                                            />
                                            {/* Кнопка помощи - скрывается когда есть символы */}
                                            {registerData.password.length === 0 && (
                                                <button
                                                    type="button"
                                                    className="help-btn"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setShowHelp(prev => ({...prev, password: !prev.password}));
                                                    }}
                                                    tabIndex={-1}
                                                >
                                                    <HelpCircle size={16}/>
                                                </button>
                                            )}

                                            {registerData.password.length > 0 && (
                                                <button
                                                    type="button"
                                                    className="password-toggle-btn"
                                                    onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                                                    tabIndex={-1}
                                                >
                                                    {showRegisterPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
                                                </button>
                                            )}
                                        </div>

                                        {showHelp.password && (
                                            <div className="help-tooltip">
                                                Пароль должен содержать:
                                                <ul>
                                                    <li>От 6 до 50 символов</li>
                                                    <li>Без пробелов</li>
                                                    <li>Рекомендуется использовать:</li>
                                                    <li>• Заглавные и строчные буквы</li>
                                                    <li>• Цифры</li>
                                                    <li>• Специальные символы: !@#$%^&*</li>
                                                    <li>• Не используйте простые пароли</li>
                                                </ul>
                                            </div>
                                        )}

                                        {registerErrors.password &&
                                            <p className="input-error">{registerErrors.password}</p>}
                                    </div>

                                    {/* Подтверждение пароля */}
                                    <div className="form-input-container password-container">
                                        <input
                                            type={showRegisterConfirmPassword ? "text" : "password"}
                                            name="confirmPassword"
                                            placeholder="Подтверждение пароля"
                                            value={registerData.confirmPassword}
                                            onChange={handleRegisterInputChange}
                                            className={`modal-input ${registerErrors.confirmPassword ? 'error' : ''}`}
                                            required
                                        />
                                        {registerData.confirmPassword.length > 0 && (
                                            <button
                                                type="button"
                                                className="password-toggle-btn"
                                                onClick={() => setShowRegisterConfirmPassword(!showRegisterConfirmPassword)}
                                                tabIndex={-1}
                                            >
                                                {showRegisterConfirmPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
                                            </button>
                                        )}
                                        {registerErrors.confirmPassword &&
                                            <p className="input-error">{registerErrors.confirmPassword}</p>}
                                    </div>

                                    {registerError &&
                                        <p className="input-error general-error">{registerError}</p>}

                                    <div className="modal-actions">
                                        <button type="submit" className="modal-submit-btn">
                                            Зарегистрироваться
                                        </button>
                                    </div>

                                    <div className="auth-switch-container">
                                        <span>Уже есть аккаунт? </span>
                                        <button
                                            type="button"
                                            className="auth-switch-link"
                                            onClick={switchToLogin}
                                        >
                                            Войти
                                        </button>
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            )}

            {showCreateAnimeModal && (
                <div className="modal-overlay">
                    <div className="modal-content create-anime-modal" ref={modalRef}>
                        {/* Переключатель режима */}
                        <div className="modal-mode-switcher">
                            <button
                                type="button"
                                className={`mode-switch-btn ${animeModalMode === 'create' ? 'active' : ''}`}
                                onClick={() => handleModeSwitch('create')}
                            >
                                Создать аниме
                            </button>
                            <button
                                type="button"
                                className={`mode-switch-btn ${animeModalMode === 'edit' ? 'active' : ''}`}
                                onClick={() => handleModeSwitch('edit')}
                            >
                                Редактировать аниме
                            </button>
                        </div>


                        {/* Поиск аниме для редактирования */}
                        {animeModalMode === 'edit' && (
                            <div className="anime-search-section">
                                <div className="form-group">
                                    <label className="form-label">Поиск аниме для редактирования</label>
                                    <input
                                        type="text"
                                        placeholder="Введите название аниме..."
                                        value={searchAnimeQuery}
                                        onChange={(e) => setSearchAnimeQuery(e.target.value)}
                                        className="form-input"
                                    />
                                </div>

                                {/* Результаты поиска */}
                                {searchAnimeLoading && (
                                    <div style={{color: 'rgba(255,255,255,0.7)', textAlign: 'center'}}>
                                        Поиск...
                                    </div>
                                )}

                                {searchAnimeResults.length > 0 && (
                                    <div className="anime-search-results">
                                        {searchAnimeResults.map((anime) => (
                                            <div
                                                key={anime.id}
                                                className="anime-search-result-item"
                                                onClick={() => handleSelectAnimeForEdit(anime)}
                                            >
                                                <img
                                                    src={anime.imageUrl}
                                                    alt={anime.title}
                                                    className="anime-search-result-image"
                                                    onError={(e) => {
                                                        e.currentTarget.src = "https://images.unsplash.com/photo-1578632767115-351597cf2477?w=50&h=70&fit=crop";
                                                    }}
                                                />
                                                <div className="anime-search-result-info">
                                                                    <span
                                                                        className="anime-search-result-title">{anime.title}</span>
                                                    <span
                                                        className="anime-search-result-episodes">EP {anime.episodes}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {selectedAnimeForEdit && (
                                    <div className="selected-anime-info">
                                        <span>Редактируем: <strong>{selectedAnimeForEdit.title}</strong></span>
                                    </div>
                                )}
                            </div>
                        )}

                        <form onSubmit={handleCreateAnimeSubmit} className="create-anime-form">
                            {/* Название */}
                            <div className="form-group">
                                <label className="form-label">Название *</label>
                                <input
                                    type="text"
                                    name="title"
                                    placeholder="Введите название аниме"
                                    value={createAnimeData.title}
                                    onChange={handleCreateAnimeChange}
                                    className={`form-input ${createAnimeErrors.title ? 'error' : ''}`}
                                    maxLength={300}
                                />
                                {createAnimeErrors.title &&
                                    <p className="input-error-create">{createAnimeErrors.title}</p>}
                            </div>

                            {/* URL изображения */}
                            <div className="form-group">
                                <label className="form-label">Название папки с изображением *</label>
                                <input
                                    type="text"
                                    name="imageFolder"
                                    placeholder="Например: Ван-пис, Волейбол..."
                                    value={createAnimeData.imageFolder}
                                    onChange={handleCreateAnimeChange}
                                    className={`form-input ${createAnimeErrors.imageFolder ? 'error' : ''}`}
                                />
                                <div style={{
                                    color: 'rgba(255,255,255,0.6)',
                                    fontSize: '12px',
                                    marginTop: '5px'
                                }}>
                                    Название папки в директории "Постеры аниме".
                                </div>
                                {createAnimeErrors.imageFolder &&
                                    <p className="input-error-create">{createAnimeErrors.imageFolder}</p>}
                            </div>

                            {/* Количество эпизодов */}
                            <div className="form-group">
                                <label className="form-label">Количество эпизодов *</label>
                                <input
                                    type="number"
                                    name="episodes"
                                    placeholder="Введите количество эпизодов"
                                    value={createAnimeData.episodes}
                                    onChange={handleCreateAnimeChange}
                                    className={`form-input ${createAnimeErrors.episodes ? 'error' : ''}`}
                                    min="1"
                                />
                                {createAnimeErrors.episodes &&
                                    <p className="input-error-create">{createAnimeErrors.episodes}</p>}
                            </div>

                            {/* Год выпуска */}
                            <div className="form-group">
                                <label className="form-label">Дата выхода *</label>
                                <input
                                    type="date"
                                    name="releaseDate"
                                    placeholder="Выберите дату выхода"
                                    value={createAnimeData.releaseDate}
                                    onChange={handleCreateAnimeChange}
                                    className={`form-input ${createAnimeErrors.releaseDate ? 'error' : ''}`}
                                    required
                                />
                            </div>

                            {/* Статус аниме - ТАК ЖЕ КАК ЖАНРЫ */}
                            <div className="form-group">
                                <label className="form-label">Статус аниме *</label>
                                <div className="status-container">
                                    <div className="status-grid">
                                        <div>
                                            <input
                                                type="radio"
                                                id="status-ongoing"
                                                name="animeStatus"
                                                className="status-radio"
                                                checked={createAnimeData.animeStatus === AnimeStatus.ONGOING}
                                                onChange={() => handleCreateAnimeChange({
                                                    target: {
                                                        name: 'animeStatus',
                                                        value: AnimeStatus.ONGOING
                                                    }
                                                } as any)}
                                            />
                                            <label htmlFor="status-ongoing" className="status-label">
                                                Онгоинг
                                            </label>
                                        </div>
                                        <div>
                                            <input
                                                type="radio"
                                                id="status-completed"
                                                name="animeStatus"
                                                className="status-radio"
                                                checked={createAnimeData.animeStatus === AnimeStatus.COMPLETED}
                                                onChange={() => handleCreateAnimeChange({
                                                    target: {
                                                        name: 'animeStatus',
                                                        value: AnimeStatus.COMPLETED
                                                    }
                                                } as any)}
                                            />
                                            <label htmlFor="status-completed" className="status-label">
                                                Вышел
                                            </label>
                                        </div>
                                        <div>
                                            <input
                                                type="radio"
                                                id="status-upcoming"
                                                name="animeStatus"
                                                className="status-radio"
                                                checked={createAnimeData.animeStatus === AnimeStatus.UPCOMING}
                                                onChange={() => handleCreateAnimeChange({
                                                    target: {
                                                        name: 'animeStatus',
                                                        value: AnimeStatus.UPCOMING
                                                    }
                                                } as any)}
                                            />
                                            <label htmlFor="status-upcoming" className="status-label">
                                                Анонсирован
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Жанры */}
                            <div className="form-group">
                                <label className="form-label">Жанры *</label>

                                {/* Поле поиска жанров */}
                                <div className="genre-search-container">
                                    <input
                                        type="text"
                                        placeholder="Поиск жанров..."
                                        value={searchGenreQuery}
                                        onChange={(e) => {
                                            setSearchGenreQuery(e.target.value);
                                            setShowGenreSearch(true);
                                        }}
                                        onFocus={() => setShowGenreSearch(true)}
                                        className="genre-search-input"
                                    />

                                    {/* Индикатор загрузки поиска */}
                                    {searchGenreLoading && showGenreSearch && (
                                        <div className="search-loading-indicator">
                                            Поиск...
                                        </div>
                                    )}
                                </div>

                                <div className="genres-container">
                                    <div className="genres-grid">
                                        {genresLoading ? (
                                            <div style={{
                                                color: 'rgba(255,255,255,0.7)',
                                                textAlign: 'center',
                                                gridColumn: '1/-1'
                                            }}>
                                                Загрузка жанров...
                                            </div>
                                        ) : (
                                            // Показываем либо результаты поиска, либо все жанры
                                            (showGenreSearch && searchGenreQuery.trim() ? searchGenreResults : genres).map(genre => (
                                                <div key={genre.id}>
                                                    <input
                                                        type="checkbox"
                                                        id={`genre-${genre.id}`}
                                                        className="genre-checkbox"
                                                        checked={createAnimeData.selectedGenres.includes(genre.id)}
                                                        onChange={() => handleGenreChange(genre.id)}
                                                    />
                                                    <label
                                                        htmlFor={`genre-${genre.id}`}
                                                        className="genre-label"
                                                    >
                                                        {genre.name}
                                                    </label>
                                                </div>
                                            ))
                                        )}
                                    </div>

                                    {/* Сообщение, если поиск не дал результатов */}
                                    {showGenreSearch && searchGenreQuery.trim() && searchGenreResults.length === 0 && !searchGenreLoading && (
                                        <div style={{
                                            color: 'rgba(255,255,255,0.5)',
                                            textAlign: 'center',
                                            padding: '10px',
                                            fontSize: '13px'
                                        }}>
                                            Жанры не найдены
                                        </div>
                                    )}

                                    <div className="selected-genres-count">
                                        Выбрано: {createAnimeData.selectedGenres.length}
                                    </div>
                                    {createAnimeErrors.genres && (
                                        <p className="input-error-create">{createAnimeErrors.genres}</p>
                                    )}
                                </div>
                            </div>

                            {/* Студии */}
                            <div className="form-group">
                                <label className="form-label">Студии *</label>

                                {/* Поле поиска студий */}
                                <div className="studio-search-container">
                                    <input
                                        type="text"
                                        placeholder="Поиск студий..."
                                        value={searchStudioQuery}
                                        onChange={(e) => {
                                            setSearchStudioQuery(e.target.value);
                                            setShowStudioSearch(true);
                                        }}
                                        onFocus={() => setShowStudioSearch(true)}
                                        className="studio-search-input"
                                    />

                                    {/* Индикатор загрузки поиска */}
                                    {searchStudioLoading && showStudioSearch && (
                                        <div className="search-loading-indicator">
                                            Поиск...
                                        </div>
                                    )}
                                </div>

                                <div className="studios-container">
                                    <div className="studios-grid">
                                        {studiosLoading ? (
                                            <div style={{
                                                color: 'rgba(255,255,255,0.7)',
                                                textAlign: 'center',
                                                gridColumn: '1/-1'
                                            }}>
                                                Загрузка студий...
                                            </div>
                                        ) : (
                                            // Показываем либо результаты поиска, либо все студии
                                            (showStudioSearch && searchStudioQuery.trim() ? searchStudioResults : studios).map(studio => (
                                                <div key={studio.id}>
                                                    <input
                                                        type="checkbox"
                                                        id={`studio-${studio.id}`}
                                                        className="studio-checkbox"
                                                        checked={createAnimeData.selectedStudios.includes(studio.id)}
                                                        onChange={() => handleStudioChange(studio.id)}
                                                    />
                                                    <label
                                                        htmlFor={`studio-${studio.id}`}
                                                        className="studio-label"
                                                    >
                                                        {studio.name}
                                                    </label>
                                                </div>
                                            ))
                                        )}
                                    </div>

                                    {/* Сообщение, если поиск не дал результатов */}
                                    {showStudioSearch && searchStudioQuery.trim() && searchStudioResults.length === 0 && !searchStudioLoading && (
                                        <div style={{
                                            color: 'rgba(255,255,255,0.5)',
                                            textAlign: 'center',
                                            padding: '10px',
                                            fontSize: '13px'
                                        }}>
                                            Студии не найдены
                                        </div>
                                    )}

                                    <div className="selected-studios-count">
                                        Выбрано: {createAnimeData.selectedStudios.length}
                                    </div>
                                    {createAnimeErrors.studios && (
                                        <p className="input-error-create">{createAnimeErrors.studios}</p>
                                    )}
                                </div>
                            </div>

                            {/* Первоисточник */}
                            <div className="form-group">
                                <label className="form-label">Первоисточник</label>
                                <input
                                    type="text"
                                    name="sourceMaterial"
                                    placeholder="Например: манга, ранобэ, оригинал"
                                    value={createAnimeData.sourceMaterial}
                                    onChange={handleSourceMaterialChange}
                                    className={`form-input ${createAnimeErrors.sourceMaterial ? 'error' : ''}`}
                                    maxLength={100}
                                />
                                {createAnimeErrors.sourceMaterial &&
                                    <p className="input-error-create">{createAnimeErrors.sourceMaterial}</p>}
                            </div>

                            {/* Возрастной рейтинг */}
                            <div className="form-group">
                                <label className="form-label">Возрастной рейтинг</label>
                                <div className="anime-page-header-dropdown-container" ref={ageRatingDropdownRef}>
                                    <button
                                        type="button"
                                        className={`anime-page-header-dropdown-button ${showAgeRatingDropdown ? 'open' : ''}`}
                                        onClick={() => setShowAgeRatingDropdown(!showAgeRatingDropdown)}
                                    >
                                        <span>
                                            {createAnimeData.ageRating
                                                ? formatAgeRating(createAnimeData.ageRating)
                                                : 'Выберите рейтинг'
                                            }
                                        </span>
                                        <ChevronDown className="chevron" size={16}/>
                                    </button>
                                    {showAgeRatingDropdown && (
                                        <div className="anime-page-header-dropdown-menu">
                                            <div
                                                className={`anime-page-header-dropdown-option ${createAnimeData.ageRating === '' ? 'selected' : ''}`}
                                                onClick={() => handleAgeRatingSelect('')}
                                            >
                                                Не выбран
                                            </div>
                                            {ageRatingsLoading ? (
                                                <div className="anime-page-header-dropdown-option"
                                                     style={{color: 'var(--text-secondary)', textAlign: 'center'}}>
                                                    Загрузка...
                                                </div>
                                            ) : (
                                                ageRatings.map(rating => (
                                                    <div
                                                        key={rating.code}
                                                        className={`anime-page-header-dropdown-option ${createAnimeData.ageRating === rating.code ? 'selected' : ''}`}
                                                        onClick={() => handleAgeRatingSelect(rating.code)}
                                                    >
                                                        {formatAgeRating(rating.code)}
                                                        {rating.description && (
                                                            <span style={{
                                                                fontSize: '12px',
                                                                marginLeft: '8px'
                                                            }}>
                                                                ({rating.description})
                                                            </span>
                                                        )}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>
                                {createAnimeErrors.ageRating &&
                                    <p className="input-error-create">{createAnimeErrors.ageRating}</p>}
                            </div>

                            {/* Описание */}
                            <div className="form-group">
                                <label className="form-label">Описание</label>
                                <textarea
                                    name="description"
                                    placeholder="Введите описание аниме (максимум 2000 символов)"
                                    value={createAnimeData.description}
                                    onChange={handleCreateAnimeChange}
                                    className={`form-input form-textarea ${createAnimeErrors.description ? 'error' : ''}`}
                                    maxLength={2000}
                                    rows={4}
                                />
                                <div style={{
                                    color: 'rgba(255,255,255,0.6)',
                                    fontSize: '12px',
                                    textAlign: 'right'
                                }}>
                                    {createAnimeData.description.length}/2000
                                </div>
                                {createAnimeErrors.description &&
                                    <p className="input-error-create">{createAnimeErrors.description}</p>}
                            </div>

                            {createAnimeErrors.general && (
                                <p className="input-error-create general-error-create">{createAnimeErrors.general}</p>
                            )}

                            <div className="modal-actions-row">
                                <button
                                    type="button"
                                    className="modal-submit-btn-create"
                                    onClick={handleCloseCreateAnime}
                                    disabled={createAnimeLoading}
                                >
                                    Отмена
                                </button>
                                <button
                                    type="submit"
                                    className="modal-submit-btn-create primary"
                                    disabled={createAnimeLoading || (animeModalMode === 'edit' && !selectedAnimeForEdit)}
                                >
                                    {createAnimeLoading
                                        ? (animeModalMode === 'create' ? 'Создание...' : 'Обновление...')
                                        : (animeModalMode === 'create' ? 'Создать аниме' : 'Обновить аниме')
                                    }
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default Header;